import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";

import {
    SESSION_PAYMENT_STATUSES,
    SESSION_PROGRESS,
    Session,
    SessionPaymentStatus,
    SessionProgress,
    SessionRequester,
} from "../../../domain/Session";
import { sessionService } from "../../../application/SessionApplicationService";

type StatusChange =
    | Pick<Session, "session_status" | "cancelled_by">
    | Pick<Session, "payment_status">;

interface SessionStatusControlsProps {
    sessionId: string;
    sessionStatus: SessionProgress;
    paymentStatus: SessionPaymentStatus;
    /** For a cancelled session, who asked for it. */
    cancelledBy?: SessionRequester | null;
    onChange: (changes: StatusChange) => void;
}

// A cancellation the patient asked for is its own choice in the picker: it is
// what the reports count as owed, and the therapist is the one who knows.
const CANCELLED_BY_PATIENT = "cancelled:patient";

const progressValue = (status: SessionProgress, cancelledBy?: SessionRequester | null) =>
    status === "cancelled" && cancelledBy === "patient" ? CANCELLED_BY_PATIENT : status;

const progressChange = (value: string): Pick<Session, "session_status" | "cancelled_by"> => {
    if (value === CANCELLED_BY_PATIENT) return { session_status: "cancelled", cancelled_by: "patient" };
    if (value === "cancelled") return { session_status: "cancelled", cancelled_by: "therapist" };
    return { session_status: value as SessionProgress };
};

const CHIP =
    "appearance-none cursor-pointer rounded-full border py-1 pl-3 pr-7 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-wait disabled:opacity-60";

const PROGRESS_CHIP: Record<SessionProgress, string> = {
    pending: "border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300",
    finished: "border-green-200 bg-green-50 text-green-700 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-400",
    cancelled: "border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400",
};

const PAYMENT_CHIP: Record<SessionPaymentStatus, string> = {
    pending: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-400",
    paid: "border-green-200 bg-green-50 text-green-700 dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-400",
};

/**
 * Whether the session took place and whether it was paid, as chips the
 * therapist taps to change: a session held without notes, one called off,
 * one paid in cash. A native select underneath, so a phone opens its own
 * picker. Both also follow on their own — a call held or a recording of it
 * finishes a session, a paid payment pays it; notes alone never do.
 */
export default function SessionStatusControls({
    sessionId,
    sessionStatus,
    paymentStatus,
    cancelledBy,
    onChange,
}: SessionStatusControlsProps) {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);

    const save = async (changes: StatusChange) => {
        setSaving(true);
        setSaveFailed(false);
        try {
            await sessionService.updateSession(sessionId, changes);
            onChange(changes);
        } catch {
            setSaveFailed(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <span className="relative inline-flex">
                <label htmlFor={`session-progress-${sessionId}`} className="sr-only">
                    {t("session.progressStatus")}
                </label>
                <select
                    id={`session-progress-${sessionId}`}
                    className={`${CHIP} ${PROGRESS_CHIP[sessionStatus]}`}
                    value={progressValue(sessionStatus, cancelledBy)}
                    disabled={saving}
                    onChange={(event) => save(progressChange(event.target.value))}
                >
                    {SESSION_PROGRESS.map((status) => (
                        <option key={status} value={status}>
                            {t(`session.progressOptions.${status}`)}
                        </option>
                    ))}
                    <option value={CANCELLED_BY_PATIENT}>{t("reports.cancel.requestedByPatient")}</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 opacity-70" aria-hidden="true" />
            </span>
            <span className="relative inline-flex">
                <label htmlFor={`session-payment-${sessionId}`} className="sr-only">
                    {t("session.paymentStatus")}
                </label>
                <select
                    id={`session-payment-${sessionId}`}
                    className={`${CHIP} ${PAYMENT_CHIP[paymentStatus]}`}
                    value={paymentStatus}
                    disabled={saving}
                    onChange={(event) => save({ payment_status: event.target.value as SessionPaymentStatus })}
                >
                    {SESSION_PAYMENT_STATUSES.map((status) => (
                        <option key={status} value={status}>
                            {t(`session.paymentOptions.${status}`)}
                        </option>
                    ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 opacity-70" aria-hidden="true" />
            </span>
            {saveFailed && (
                <p role="alert" className="basis-full text-sm text-red-600 dark:text-red-400">
                    {t("session.statusSaveFailed")}
                </p>
            )}
        </>
    );
}
